import { ContactAddressService } from '../../api/services/ContactAddressService';
import type { Routemize_DTOs_ContactAddressDTO } from '../../api/models/Routemize_DTOs_ContactAddressDTO';
import type { Routemize_DTOs_CreateContactAddressDTO } from '../../api/models/Routemize_DTOs_CreateContactAddressDTO';
import type { Routemize_DTOs_UpdateContactAddressDTO } from '../../api/models/Routemize_DTOs_UpdateContactAddressDTO';
import type { UserAddress, AddressValidationResult } from '../users/AddressManager';

/**
 * ContactAddressManager - Business logic for contact address management
 * Implements the same interface as AddressManager but delegates to ContactAddressService
 * This allows the AddressManager component to work with both user and contact addresses
 */
export class ContactAddressManager {
  /**
   * Get all addresses for a specific contact
   * Maps ContactAddressDTO to UserAddress format for UI compatibility
   */
  async getUserAddresses(contactId: string): Promise<UserAddress[]> {
    const addresses = await ContactAddressService.getApiAppContactAddressGetContactAddresses(contactId);

    // Map ContactAddressDTO to UserAddress format
    return (addresses || []).map(dto => this.mapContactAddressToUserAddress(dto));
  }

  /**
   * Add a single address to contact's existing addresses
   */
  async addUserAddress(contactId: string, address: UserAddress): Promise<UserAddress> {
    // Validate the new address
    const validation = this.validateAddress(address);
    if (!validation.isValid) {
      throw new Error(`Address validation failed: ${validation.errors.join(', ')}`);
    }

    // Map UserAddress to CreateContactAddressDTO
    const createDto: Routemize_DTOs_CreateContactAddressDTO = {
      contactId,
      addressLine1: address.addressLine1 || null,
      addressLine2: address.addressLine2 || null,
      city: address.city || null,
      state: address.state || null,
      country: address.country ?? null,
      zipCode: address.zipCode || address.postalCode || null,
      latitude: address.latitude || null,
      longitude: address.longitude || null,
      isActive: address.isActive ?? true
    };

    // Call API to create address
    const createdDto = await ContactAddressService.postApiAppContactAddressCreate(createDto);

    // Map response back to UserAddress and return
    return this.mapContactAddressToUserAddress(createdDto);
  }

  /**
   * Update a specific address
   */
  async updateUserAddress(contactId: string, addressId: string, updatedAddress: UserAddress): Promise<void> {
    // Validate the updated address
    const validation = this.validateAddress(updatedAddress);
    if (!validation.isValid) {
      throw new Error(`Address validation failed: ${validation.errors.join(', ')}`);
    }

    // Map UserAddress to UpdateContactAddressDTO
    const updateDto: Routemize_DTOs_UpdateContactAddressDTO = {
      id: addressId,
      contactId,
      addressLine1: updatedAddress.addressLine1 || null,
      addressLine2: updatedAddress.addressLine2 || null,
      city: updatedAddress.city || null,
      state: updatedAddress.state || null,
      country: updatedAddress.country ?? null,
      zipCode: updatedAddress.zipCode || updatedAddress.postalCode || null,
      latitude: updatedAddress.latitude || null,
      longitude: updatedAddress.longitude || null,
      isActive: updatedAddress.isActive ?? true
    };

    // Call API to update address
    await ContactAddressService.putApiAppContactAddressUpdate(updateDto);
  }

  /**
   * Delete a specific address
   */
  async deleteUserAddress(contactId: string, addressId: string): Promise<void> {
    await ContactAddressService.deleteApiAppContactAddressDelete(addressId);
  }

  /**
   * Find an existing contact address matching the given fields, or create a
   * new one. Prevents duplicate-key errors when the same address already
   * exists for the contact. Returns the address ID, or null if creation
   * succeeded but returned no ID.
   */
  async findOrCreateContactAddress(params: {
    contactId: string;
    addressLine1: string;
    addressLine2?: string | null;
    city: string;
    state: string;
    zipCode: string;
    country?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  }): Promise<string | null> {
    const normalize = (s: string | null | undefined) => (s || '').trim().toLowerCase();

    const findMatch = (addresses: Routemize_DTOs_ContactAddressDTO[] | null | undefined) =>
      addresses?.find(a =>
        normalize(a.addressLine1) === normalize(params.addressLine1) &&
        normalize(a.city) === normalize(params.city) &&
        normalize(a.state) === normalize(params.state) &&
        normalize(a.zipCode) === normalize(params.zipCode)
      );

    try {
      const existingAddresses = await ContactAddressService.getApiAppContactAddressGetContactAddresses(params.contactId);
      const match = findMatch(existingAddresses);
      if (match?.id) {
        return match.id;
      }
    } catch {
      // Lookup failure is non-fatal — fall through to creation.
    }

    try {
      const createDto: Routemize_DTOs_CreateContactAddressDTO = {
        contactId: params.contactId,
        addressLine1: params.addressLine1 || null,
        addressLine2: params.addressLine2 || null,
        city: params.city || null,
        state: params.state || null,
        zipCode: params.zipCode || null,
        country: params.country || null,
        latitude: params.latitude ?? null,
        longitude: params.longitude ?? null,
        isActive: true
      };
      const created = await ContactAddressService.postApiAppContactAddressCreate(createDto);
      if (created.id) {
        return created.id;
      }
    } catch (createError: unknown) {
      // If creation raced another writer and hit a duplicate key, re-lookup.
      const err = createError as { status?: number; body?: { error?: { details?: string } } };
      if (err?.status === 500 && err?.body?.error?.details?.includes('duplicate key')) {
        try {
          const existingAddresses = await ContactAddressService.getApiAppContactAddressGetContactAddresses(params.contactId);
          const match = findMatch(existingAddresses);
          if (match?.id) {
            return match.id;
          }
        } catch {
          // Fall through to rethrow the original creation error.
        }
      }
      throw createError;
    }

    return null;
  }

  /**
   * Validate address data with basic rules
   * Reuses validation logic compatible with UserAddress interface
   */
  validateAddress(address: UserAddress): AddressValidationResult {
    const errors: string[] = [];

    // Required field validation
    if (!address.addressLine1?.trim()) {
      errors.push('Address Line 1 is required');
    }

    if (!address.city?.trim()) {
      errors.push('City is required');
    }

    // Address line length validation
    if (address.addressLine1 && address.addressLine1.length > 100) {
      errors.push('Address Line 1 must be 100 characters or less');
    }

    if (address.addressLine2 && address.addressLine2.length > 100) {
      errors.push('Address Line 2 must be 100 characters or less');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Map ContactAddressDTO to UserAddress format
   * Handles differences between contact and user address DTOs
   */
  private mapContactAddressToUserAddress(dto: Routemize_DTOs_ContactAddressDTO): UserAddress {
    return {
      id: dto.id,
      addressLine1: dto.addressLine1 || undefined,
      addressLine2: dto.addressLine2 || undefined,
      city: dto.city || undefined,
      state: dto.state || undefined,
      zipCode: dto.zipCode || undefined,
      postalCode: dto.zipCode || undefined, // Map zipCode to postalCode for compatibility
      country: dto.country || 'US', // Use stored country when available; fall back to US
      latitude: dto.latitude || undefined,
      longitude: dto.longitude || undefined,
      isActive: dto.isActive ?? true,
      addressType: 'Other', // Default address type since ContactAddressDTO doesn't have this field
      fullAddress: dto.fullAddress || undefined
    };
  }
}

// Export singleton instance
export const contactAddressManager = new ContactAddressManager();
